import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { selectDatasourceRows, selectWidgetEffectiveCols, setCrossDatasourceFilterModel, removeCrossDatasourceFilterModel } from '../../redux/datasourceSlice.js';
import { useDatasource } from '../../hooks/useDatasource.js';
import { useDatasourceContext } from '../base/DatasourceEngineGrid.jsx';
import { selectUsesDatasourceV2 } from '../../datasource_v2/selectors.js';

const EMPTY_SETTINGS = {};

const VERTICAL_MAP   = { top: 'flex-start', center: 'center', bottom: 'flex-end' };
const HORIZONTAL_MAP = { left: 'flex-start', center: 'center', right: 'flex-end' };

function agFilterType(dataType) {
  if (['integer', 'decimal', 'formula'].includes(dataType)) return 'number';
  return 'text';
}

function buildCondition(colId, value, filterType) {
  return { filterType, type: 'equals', filter: filterType === 'number' ? Number(value) : String(value), colId };
}

function ButtonFilterWidget({ widgetId }) {
  const dispatch = useDispatch();
  const [activeValues, setActiveValues] = useState([]);

  // v2 datasources provide server side distinct values through the context, keyed 'v2-<id>'
  const datasourceContext = useDatasourceContext();
  const usesDatasourceV2 = useSelector(selectUsesDatasourceV2(widgetId));
  const legacyDatasourceId = useSelector(state => state.dashboard.widgets[widgetId]?.data_datasource?.id);
  const datasourceId = usesDatasourceV2 ? datasourceContext.datasourceId : legacyDatasourceId;
  const settings = useSelector(state => state.dashboard.widgets[widgetId]?.settings ?? EMPTY_SETTINGS);

  // Populate Redux with fresh datasource rows. The bulk fetchDatasourcesForDashboard may return
  // null rows for new datasources that haven't been refreshed yet; this per-widget live fetch
  // ensures selectDatasourceRows always has data once the API responds.
  useDatasource({ widgetId, live: true });

  const {
    columnId,
    bgColor = '#f0f0f0',
    fgColor = '#333333',
    activeBgColor = '#4cb6b2',
    activeFgColor = '#ffffff',
    buttonFontSize = 14,
    paddingV = 10,
    paddingH = 14,
    gap = 8,
    borderRadius = 4,
    showAllButton = false,
    allButtonLabel,
    wrap = true,
    label,
    labelFontSize = 14,
    labelColor,
    verticalAlign = 'top',
    horizontalAlign = 'left',
  } = settings;

  const rawRows = useSelector(selectDatasourceRows(datasourceId, widgetId));
  const cols = useSelector(selectWidgetEffectiveCols(widgetId));

  const colMeta = useMemo(() => cols.find(c => c.accessor === columnId), [cols, columnId]);
  const filterType = useMemo(() => agFilterType(colMeta?.data_type), [colMeta]);

  const uniqueValues = useMemo(() => {
    if (usesDatasourceV2) return (datasourceContext.values || []).filter(v => v != null && v !== '');
    if (!columnId || !rawRows?.length) return [];
    const seen = new Set();
    const values = [];
    for (const row of rawRows) {
      const val = row?.[columnId];
      if (val != null && val !== '' && !seen.has(val)) {
        seen.add(val);
        values.push(val);
      }
    }
    return values;
  }, [rawRows, columnId, usesDatasourceV2, datasourceContext.values]);

  const filterId = `button-filter-${widgetId}`;

  useEffect(() => {
    return () => {
      dispatch(removeCrossDatasourceFilterModel({ filterId }));
    };
  }, [dispatch, filterId]);

  const applyFilter = (values) => {
    if (!datasourceId || !columnId) return;
    if (!values.length) {
      dispatch(removeCrossDatasourceFilterModel({ filterId }));
      return;
    }
    const filterModel = values.length === 1
      ? buildCondition(columnId, values[0], filterType)
      : { filterType: 'join', type: 'OR', conditions: values.map(v => buildCondition(columnId, v, filterType)) };
    dispatch(setCrossDatasourceFilterModel({ datasourceId, filterId, filterModel }));
  };

  const toggleValue = (value) => {
    const next = activeValues.includes(value)
      ? activeValues.filter(v => v !== value)
      : [...activeValues, value];
    setActiveValues(next);
    applyFilter(next);
  };

  const clearAll = () => {
    setActiveValues([]);
    dispatch(removeCrossDatasourceFilterModel({ filterId }));
  };

  const buttonStyle = (isActive) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: `${paddingV}px ${paddingH}px`,
    backgroundColor: isActive ? activeBgColor : bgColor,
    color: isActive ? activeFgColor : fgColor,
    fontSize: `${buttonFontSize}px`,
    borderRadius: `${borderRadius}px`,
    border: 'none',
    cursor: 'pointer',
    transition: 'background-color 0.15s, color 0.15s',
    userSelect: 'none',
  });

  const allActive = !activeValues.length;

  return (
    <div style={{
      display: 'flex',
      width: '100%',
      height: '100%',
      overflow: 'auto',
      alignItems: VERTICAL_MAP[verticalAlign] ?? 'flex-start',
      justifyContent: HORIZONTAL_MAP[horizontalAlign] ?? 'flex-start',
    }}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
        {label ? (
          <div style={{
            fontSize: `${labelFontSize}px`,
            color: labelColor || undefined,
            fontWeight: 'bold',
          }}>
            {label}
          </div>
        ) : null}
        <div style={{
          display: 'flex',
          flexWrap: wrap ? 'wrap' : 'nowrap',
          gap: `${gap}px`,
        }}>
          {showAllButton && (
            <button style={buttonStyle(allActive)} onClick={clearAll}>
              {allButtonLabel || I18n.t('all')}
            </button>
          )}
          {uniqueValues.map((value) => (
            <button
              key={value}
              style={buttonStyle(activeValues.includes(value))}
              onClick={() => toggleValue(value)}
            >
              {value}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

export default ButtonFilterWidget;
